import FVector from '../../../data/geometry/FVector';

export class BinaryReader {
    private dataView: DataView;
    private position: number;
    chars: string[];

    private limit: number;
    private capacity: number;

    constructor(data: ArrayBuffer) {
        this.dataView = new DataView(data);
        this.position = this.dataView.byteOffset;
        this.chars = new Array(32);

        this.limit = this.dataView.byteOffset + this.dataView.byteLength;
        this.capacity = this.dataView.byteLength;
    }

    getData() {
        return this.dataView.buffer;
    }

    reset() {
        this.setPos(0);
    }

    skip(bytes: number) {
        this.position += bytes;
    }

    getPos() {
        return this.position;
    }
    setPos(pos: number) {
        this.position = pos;
    }

    readBoolean(): boolean {
        if (this.position + 1 > this.dataView.byteLength) {
            throw new Error('Offset is outside the bounds of the DataView (readBoolean)');
        }
        const value = this.dataView.getInt8(this.position);
        this.position += 1;
        if (value != 0) return true;
        else return false;
    }

    readInt8() {
        if (this.position + 1 > this.dataView.byteLength) {
            throw new Error('Offset is outside the bounds of the DataView (readUInt8)');
        }
        const value = this.dataView.getInt8(this.position);
        this.position += 1;
        return value;
    }

    readUInt8() {
        if (this.position + 1 > this.dataView.byteLength) {
            throw new Error('Offset is outside the bounds of the DataView (readUInt8)');
        }
        const value = this.dataView.getUint8(this.position);
        this.position += 1;
        return value;
    }

    readInt32() {
        if (this.position + 4 > this.dataView.byteLength) {
            throw new Error('Offset is outside the bounds of the DataView (readInt32)');
        }
        const value = this.dataView.getInt32(this.position, true);
        this.position += 4;
        return value;
    }

    readUInt32() {
        if (this.position + 4 > this.dataView.byteLength) {
            throw new Error('Offset is outside the bounds of the DataView (readUInt32)');
        }

        const value = this.dataView.getUint32(this.position, true);
        this.position += 4;
        return value;
    }

    readUInt16() {
        if (this.position + 2 > this.dataView.byteLength) {
            throw new Error('Offset is outside the bounds of the DataView (readUInt16)');
        }
        const value = this.dataView.getUint16(this.position, true);
        this.position += 2;
        return value;
    }

    readFloat32() {
        if (this.position + 4 > this.dataView.byteLength) {
            throw new Error('Offset is outside the bounds of the DataView (readFloat32)' + this.position + ' ' + this.dataView.byteLength);
        }
        const value = this.dataView.getFloat32(this.position, true);
        this.position += 4;
        return value;
    }

    readUInt64() {
        if (this.position + 8 > this.dataView.byteLength) {
            throw new Error('Offset is outside the bounds of the DataView (readUInt64)' + this.position + ' ' + this.dataView.byteLength);
        }
        const value = this.dataView.getBigUint64(this.position, true);
        this.position += 8;
        return value;
    }

    readVec3() {
        var p = this.readFVector();
        return p;
    }

    readFVector() {
        const x = this.readFloat32();
        const y = this.readFloat32();
        const z = this.readFloat32();
        var p = new FVector(x, y, z);
        return p;
    }

    readFloat64() {
        if (this.position + 8 > this.dataView.byteLength) {
            // addLog(LOG_ERROR, 'Offset is outside the bounds of the DataView (readFloat64)' + this.position + " " + this.dataView.byteLength);
            return -1;
        }
        const value = this.dataView.getFloat64(this.position, true);
        this.position += 8;
        return value;
    }

    readByte() {
        return this.dataView.getInt8(this.position);
    }

    readMSB() {
        this.require(1);
        const flag = (this.readByte() & 0x80) !== 0;
        return flag;
    }

    readVarIntFlagValue(optimizePositive: boolean) {
        const buffer = new Uint8Array(this.dataView.buffer, this.dataView.byteOffset, this.dataView.byteLength);
        let b = buffer[this.position++];
        let result = b & 0x3f; // Mask first 6 bits.
        if ((b & 0x40) !== 0) {
            // Bit 7 means another byte, bit 8 means UTF8.
            let p = this.position;
            b = buffer[p++];
            result |= (b & 0x7f) << 6;
            if ((b & 0x80) !== 0) {
                b = buffer[p++];
                result |= (b & 0x7f) << 13;
                if ((b & 0x80) !== 0) {
                    b = buffer[p++];
                    result |= (b & 0x7f) << 20;
                    if ((b & 0x80) !== 0) {
                        b = buffer[p++];
                        result |= (b & 0x7f) << 27;
                    }
                }
            }
            this.position = p;
        }

        return optimizePositive ? result : (result >>> 1) ^ -(result & 1);
    }

    toSignedByte(value: number) {
        return value;
    }

    readAsciiString() {
        const chars = this.chars;
        const buffer = new Uint8Array(this.dataView.buffer, this.dataView.byteOffset, this.dataView.byteLength);
        let p = this.position;
        let charCount = 0;
        for (let n = Math.min(chars.length, this.limit - this.position); charCount < n; charCount++, p++) {
            let b = buffer[p];
            if ((b & 0x80) === 0x80) {
                this.position = p + 1;
                chars[charCount] = String.fromCharCode(b & 0x7f);
                return chars.join('').substring(0, charCount + 1);
            }
            chars[charCount] = String.fromCharCode(b);
        }
        console.log(chars);
        this.position = p;
        return this.readAscii_slow(charCount);
    }

    readAscii_slow(charCount: number) {
        let chars = this.chars;

        while (true) {
            if (this.position === this.limit) this.require(1);
            //const b = this.toSignedByte(buffer[this.position++]);
            const b = this.readInt8();
            if (charCount === chars.length) {
                const newChars = new Array(charCount * 2);
                for (let i = 0; i < charCount; i++) {
                    newChars[i] = chars[i];
                }
                chars = newChars;
                this.chars = newChars;
            }
            let firstBitFlag = (b & 0x80) === 0x80;
            if (firstBitFlag) {
                const char = String.fromCharCode(b & 0x7f);
                chars[charCount] = char;
                console.log(char, b, firstBitFlag);
                return chars.join('').substring(0, charCount + 1);
            }
            const char = String.fromCharCode(b);
            chars[charCount++] = char;
        }
    }

    // Simplified UTF-8 reading, assumes valid data and 1 or 2 byte characters.
    readUtf8Chars(charCount: number) {
        let charIndex = 0;

        const buffer = new Uint8Array(this.dataView.buffer, this.dataView.byteOffset, this.dataView.byteLength);
        let p = this.position,
            b;

        while (charIndex < charCount) {
            b = buffer[p++];

            if (b < 0) {
                p--;
                break;
            } else {
                this.chars[charIndex] = String(((b & 0x3f) << 6) | (this.readInt8() & 0x3f));
            }
            this.chars[charIndex++] = String.fromCharCode(b);
        }
        this.position = p;
    }

    readString(): string {
        this.chars = new Array(32);
        if (!this.readMSB()) return this.readAsciiString();
        const charCount = this.readVarIntFlagValue(true);

        switch (charCount) {
            case 0:
                return '';
            case 1:
                return '';
        }

        this.readUtf8Chars(charCount - 1);
        return this.chars.join('');
    }

    require(required: number) {
        let remaining = this.limit - this.position;
        if (remaining >= required) return remaining;

        if (required > this.capacity) throw new Error(`Buffer too small: capacity: ${this.capacity}, required: ${required}`);

        // Try to fill the buffer.
        if (remaining > 0) {
            throw new Error('Not implemented yet');
            // count = this.fill(this.buffer, this.limit, this.capacity - this.limit);
            // if (count === -1) throw new Error("Buffer underflow.");
            // remaining += count;
            // if (remaining >= required) {
            // 	this.limit += count;
            // 	return remaining;
            // }
        }
        throw new Error('Not implemented yet');
        // Was not enough, compact and try again.
        // this.buffer.copyWithin(0, this.position);
        // this.total += this.position;
        // this.position = 0;

        // while (true) {
        // 	count = this.fill(this.buffer, remaining, this.capacity - remaining);
        // 	if (count === -1) {
        // 		if (remaining >= required) break;
        // 		throw new Error("Buffer underflow.");
        // 	}
        // 	remaining += count;
        // 	if (remaining >= required) break; // Enough has been read.
        // }

        // this.limit = remaining;
        // return remaining;
    }
}
